import React, {Component, ReactElement, ReactNode} from 'react'
import FacilitiesRepository, {FacilitiesForPagination} from './FacilitiesRepository'
import FacilityCategory from './FacilityCategory'
import {SortDirection} from '@mui/material/TableCell'
import FacilityForAdmin from './FacilityForAdmin'
import {Nullable} from '../../app/TypeHelpers'
import {COMMON_CONSTANTS} from '../../CommonConstants'
import {SortOrder} from '../shared/SortOrder'
import {AdminPagePropsSet, createAdminPage} from '../shared/hoc/createAdminPage'
import {Messages} from '../../Messages'
import {HeadingAreaMessageParams} from '../shared/pageBaseComponents/HeadingAreaMessageComponent'
import {Information} from '../../network/response/Information'
import PPSelect from '../shared/forms/PPSelect'
import {PageMainArea} from '../shared/hoc/withAdminPageTemplate'
import {AdminDefaultButton, AdminDefaultMediumButton} from '../../styledComponents/adminPage/Buttons'
import {SearchResultCountDisplayProps} from '../../styledComponents/adminPage/SearchResultCountDisplay'
import CheckMark from '../../styledComponents/adminPage/CheckMark'
import {formatDateTimeForUpdateDate} from '../../formatters/formatDate'
import {NoWrappedTableCell} from '../../styledComponents/adminPage/Table'
import SortablePaginationTable from '../../styledComponents/adminPage/SortablePaginationTable'
import ListSearchFormArea from '../../styledComponents/adminPage/ListSearchFormArea'
import {numberOrZero} from '../../stringHelper/stringHelpers'
import {SessionStorageHelper} from '../../SessionStorageHelper'
import SearchPageActionArea from '../../styledComponents/adminPage/SearchPageActionArea'
import SortableTableHeader, {
    SortableTableHeaderCell,
} from '../../styledComponents/adminPage/SortablePaginationTableHeader'
import SortablePaginationTableBody from '../../styledComponents/adminPage/SortablePaginationTableBody'
import {flipSortDirection} from '../shared/SortDirectionHelper'
import {
    ConditionButtonCell,
    ConditionCategoryCell,
    ConditionColumNameCell,
    ConditionInputCell,
    ConditionTable,
    ConditionTableBody,
    ConditionTableRow,
} from '../../styledComponents/adminPage/ConditionTable'
import {judgeAuthorityCheck} from '../../__NoAutomatedTests/common/util/Natutil'
import {Link, TableCell, TableRow, Tooltip} from '@mui/material'
import moment from 'moment'
import {SelectChangeEvent} from '@mui/material/Select'
import {Location, NavigateFunction} from 'react-router-dom'

const KEY_CONDOMINIUM_CD: string = 'AdminSearchFacilitiesPage.condominiumCd'
const KEY_SEARCHED_CATEGORY: string = 'AdminSearchFacilitiesPage.searchedCategory'
const KEY_ROWS_PER_PAGE: string = 'AdminSearchFacilitiesPage.rowsPerPage'

interface LocationState {
    initialMessage: HeadingAreaMessageParams
}

interface Props {
    facilitiesRepo: FacilitiesRepository
    navigate: NavigateFunction
    location: Location<LocationState>
    condominiumCd: string
}

interface State {
    selectedCategory: string
    searchedCategory: string
    facilityCategories: FacilityCategory[]
    facilities: FacilityForAdmin[]
    sortOrders: SortOrder[]
    totalCount: number
    rowsPerPage: number
    page: number
    editMode: boolean
    facilityViewMode: boolean
    introductoryViewMode: boolean
}

class AdminSearchFacilitiesPage extends Component<Props & AdminPagePropsSet, State> {

    static displayName: string = 'AdminSearchFacilitiesPage'
    subSortKey: SortOrder = new SortOrder('facilityId', 'asc')

    constructor(props: Readonly<Props & AdminPagePropsSet>) {
        super(props)
        this.state = {
            selectedCategory: 'all',
            searchedCategory: 'all',
            facilityCategories: [],
            facilities: [],
            sortOrders: [
                new SortOrder('releaseSetting', 'desc'),
                new SortOrder('sortOrder', 'asc'),
                new SortOrder('updateDatetime', 'desc'),
            ],
            rowsPerPage: COMMON_CONSTANTS.ROWS_PER_PAGE_SETTINGS[0],
            totalCount: 0,
            page: COMMON_CONSTANTS.FIRST_PAGE,
            editMode: false,
            facilityViewMode: false,
            introductoryViewMode: false,
        }

        this.handleDuplicateCancelClick = this.handleDuplicateCancelClick.bind(this)
        this.handleChangeCategorySelector = this.handleChangeCategorySelector.bind(this)
        this.handleCategorySelectedByEnterKey = this.handleCategorySelectedByEnterKey.bind(this)
        this.handleSearchFacilities = this.handleSearchFacilities.bind(this)
        this.handleNewFacilityButtonClick = this.handleNewFacilityButtonClick.bind(this)
        this.handleChangePage = this.handleChangePage.bind(this)
        this.handleChangeRowsPerPage = this.handleChangeRowsPerPage.bind(this)
        this.handleSortFacilities = this.handleSortFacilities.bind(this)
    }

    async componentDidMount(): Promise<void> {
        await this.props.eventEmitter.showLoading()
        let facilityCategories: Nullable<FacilityCategory[]>
        try {
            const {facilitiesRepo, condominiumCd} = this.props
            facilityCategories = await this.props.asyncCallFunction(
                facilitiesRepo.getFacilityCategoriesByCondominiumCd.bind(facilitiesRepo, condominiumCd),
                (httpErrorOrNetworkError) => {
                    this.props.eventEmitter.showHeadingAreaMessage(httpErrorOrNetworkError)
                },
            )
        } finally {
            this.props.eventEmitter.closeLoading()
        }
        if (facilityCategories == null) {
            return
        }

        const searchedCategory: string = this.setupCondition(facilityCategories)

        const rowsPerPage: number = this.setupRowsPerPage()

        const facilities: FacilitiesForPagination =
            await this.searchFacilities(
                searchedCategory,
                this.state.sortOrders,
                rowsPerPage,
                COMMON_CONSTANTS.FIRST_PAGE_OFFSET,
            )

        const editMode: boolean = judgeAuthorityCheck('EDIT-EQUIPMENT_FACILITY')
        const facilityViewMode: boolean = judgeAuthorityCheck('VIEW-EQUIPMENT_FACILITY')
        const introductoryViewMode: boolean = judgeAuthorityCheck('VIEW-FACILITY_DESCRIPTION')
        this.setState({
            selectedCategory: searchedCategory,
            searchedCategory: searchedCategory,
            facilityCategories: facilityCategories,
            facilities: facilities.contents,
            totalCount: facilities.totalContentsCount,
            rowsPerPage: rowsPerPage,
            editMode: editMode,
            facilityViewMode: facilityViewMode,
            introductoryViewMode: introductoryViewMode,
        })

        this.showInitialMessage()
    }

    setupCondition(facilityCategories: FacilityCategory[]): string {
        const prevCondominiumCd: Nullable<string> =
            SessionStorageHelper.getStringItemOrNull(KEY_CONDOMINIUM_CD)
        let prevSearchedCategory: Nullable<string> =
            SessionStorageHelper.getStringItemOrNull(KEY_SEARCHED_CATEGORY)

        let resetCondition: boolean = false
        if (prevCondominiumCd !== null && this.props.condominiumCd !== prevCondominiumCd) {
            resetCondition = true
        } else if (![null, 'all'].includes(prevSearchedCategory)) {
            const found: FacilityCategory | undefined =
                facilityCategories.find((category) => {
                    return `${category.categoryId}` === prevSearchedCategory
                })
            if (found === undefined) {
                resetCondition = true
            }
        }
        if (resetCondition) {
            prevSearchedCategory = null
            SessionStorageHelper.removeItem(KEY_CONDOMINIUM_CD)
            SessionStorageHelper.removeItem(KEY_SEARCHED_CATEGORY)
        }

        return prevSearchedCategory || 'all'
    }

    setupRowsPerPage(): number {
        let rowsPerPage: number = SessionStorageHelper.getNumberItemWithDefault(
            KEY_ROWS_PER_PAGE,
            COMMON_CONSTANTS.ROWS_PER_PAGE_SETTINGS[0],
        )

        if (!COMMON_CONSTANTS.ROWS_PER_PAGE_SETTINGS.includes(rowsPerPage)) {
            SessionStorageHelper.removeItem(KEY_ROWS_PER_PAGE)
            rowsPerPage = COMMON_CONSTANTS.ROWS_PER_PAGE_SETTINGS[0]
        }

        return rowsPerPage
    }

    showInitialMessage(): void {
        if (!this.props.location || !this.props.location.state) {
            return
        }
        const initialMessage: HeadingAreaMessageParams = this.props.location.state.initialMessage
        this.props.navigate(this.props.location.pathname, {replace: true})
        this.props.eventEmitter.showHeadingAreaMessage(initialMessage)
    }

    beforeUserAction(): void {
        this.props.eventEmitter.clearHeadingAreaMessage()
    }

    handleChangeCategorySelector(event: SelectChangeEvent<unknown>): void {
        this.setState({
            selectedCategory: event.target.value as string,
        })
    }

    async handleCategorySelectedByEnterKey(_: React.KeyboardEvent): Promise<void> {
        await this.handleSearchFacilities()
    }

    handleNewFacilityButtonClick(): void {
        this.props.navigate(
            `/admin/condominiums/${this.props.condominiumCd}/facilities/register`,
        )
    }

    handleDuplicateButtonClick(facility: FacilityForAdmin): void {
        this.beforeUserAction()

        this.props.eventEmitter.showDialog({
            title: '確認',
            errorLevel: Messages.IFACA0013.errorLevel,
            message: Messages.IFACA0013.toString(facility.facilityName),
            buttonProps: [
                {
                    isDefault: false,
                    buttonLabel: 'キャンセル',
                    buttonHandler: this.handleDuplicateCancelClick,
                },
                {
                    isDefault: false,
                    buttonLabel: 'OK',
                    buttonHandler: this.handleDuplicateOkClick.bind(this, facility),
                },
            ],
        })
    }

    async handleDuplicateOkClick(facility: FacilityForAdmin): Promise<void> {
        this.props.eventEmitter.closeDialog()

        let information: Nullable<Information>
        await this.props.eventEmitter.showLoading()
        try {
            information = await this.props.asyncCallFunction(
                this.props.facilitiesRepo.duplicateFacility.bind(
                    this.props.facilitiesRepo, this.props.condominiumCd, facility.facilityId,
                ),
                (httpErrorOrNetworkError) => {
                    httpErrorOrNetworkError.broadcast(this.props.eventEmitter)
                },
            )
        } finally {
            this.props.eventEmitter.closeLoading()
        }
        if (information === null) {
            return
        }

        await this.handleSearchFacilities()

        await this.props.eventEmitter.showHeadingAreaMessage({
            messageLevel: information.type,
            title: information.title || null,
            detail: information.message,
        })
    }

    handleDuplicateCancelClick(): void {
        this.props.eventEmitter.closeDialog()
    }

    async handleSearchFacilities(): Promise<void> {
        this.beforeUserAction()

        SessionStorageHelper.setItem(KEY_CONDOMINIUM_CD, this.props.condominiumCd)
        SessionStorageHelper.setItem(KEY_SEARCHED_CATEGORY, this.state.selectedCategory)

        const facilities: FacilitiesForPagination =
            await this.searchFacilities(
                this.state.selectedCategory,
                this.state.sortOrders,
                this.state.rowsPerPage,
                COMMON_CONSTANTS.FIRST_PAGE_OFFSET,
            )
        this.setState({
            searchedCategory: this.state.selectedCategory,
            facilities: facilities.contents,
            totalCount: facilities.totalContentsCount,
            page: COMMON_CONSTANTS.FIRST_PAGE,
        })
    }

    async searchFacilities(
        categoryId: string,
        sortOrders: SortOrder[],
        limit: number,
        offset: number,
    ): Promise<FacilitiesForPagination> {
        let categoryIdForRepo: Nullable<string> = categoryId
        if (categoryIdForRepo === 'all') {
            categoryIdForRepo = null
        }

        await this.props.eventEmitter.showLoading()
        let facilities: Nullable<FacilitiesForPagination>
        try {
            const {facilitiesRepo, condominiumCd} = this.props
            facilities = await this.props.asyncCallFunction(
                facilitiesRepo.getFacilitiesForAdmin.bind(
                    facilitiesRepo,
                    condominiumCd,
                    categoryIdForRepo,
                    {sortOrders: [...sortOrders, this.subSortKey], limit, offset},
                ),
                (httpErrorOrNetworkError) => {
                    httpErrorOrNetworkError.broadcast(this.props.eventEmitter)
                },
            )
        } finally {
            this.props.eventEmitter.closeLoading()
        }
        if (facilities == null) {
            return {totalContentsCount: 0, contents: []}
        }
        return facilities
    }

    async handleSortFacilities(sortKey: string): Promise<void> {
        this.beforeUserAction()

        const sortOrders: SortOrder[] = [
            new SortOrder(
                sortKey,
                flipSortDirection(this.getSortDirection(sortKey), 'asc'),
            ),
        ]
        const facilities: FacilitiesForPagination =
            await this.searchFacilities(
                this.state.searchedCategory,
                sortOrders,
                this.state.rowsPerPage,
                COMMON_CONSTANTS.FIRST_PAGE_OFFSET,
            )

        this.setState({
            facilities: facilities.contents,
            sortOrders: sortOrders,
            totalCount: facilities.totalContentsCount,
            page: COMMON_CONSTANTS.FIRST_PAGE,
        })
    }

    async handleChangePage(
        event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
        newPage: number,
    ): Promise<void> {
        this.beforeUserAction()

        const offset: number = newPage * this.state.rowsPerPage
        const facilities: FacilitiesForPagination =
            await this.searchFacilities(
                this.state.searchedCategory,
                this.state.sortOrders,
                this.state.rowsPerPage,
                offset,
            )
        this.setState({
            totalCount: facilities.totalContentsCount,
            page: newPage,
            facilities: facilities.contents,
        }, () => {
            this.props.eventEmitter.scrollToContentsTop()
        })
    }

    async handleChangeRowsPerPage(event: React.ChangeEvent<HTMLInputElement>): Promise<void> {
        this.beforeUserAction()

        const rowsPerPage: number = numberOrZero(event.target.value)

        SessionStorageHelper.setItem(KEY_ROWS_PER_PAGE, `${rowsPerPage}`)

        const facilities: FacilitiesForPagination =
            await this.searchFacilities(
                this.state.searchedCategory,
                this.state.sortOrders,
                rowsPerPage,
                COMMON_CONSTANTS.FIRST_PAGE_OFFSET,
            )
        this.setState({
            facilities: facilities.contents,
            totalCount: facilities.totalContentsCount,
            rowsPerPage: rowsPerPage,
            page: COMMON_CONSTANTS.FIRST_PAGE,
        }, () => {
            this.props.eventEmitter.scrollToContentsTop()
        })
    }

    getSortDirection(sortKey: string): SortDirection {
        const sortOrder: SortOrder | undefined =
            this.state.sortOrders.find(order => order.sortKey === sortKey)
        if (sortOrder === undefined) {
            return false
        }
        return sortOrder.sortDirection
    }

    searchResults(): SearchResultCountDisplayProps {
        if (this.state.facilities.length === 0) {
            if (this.state.selectedCategory === 'all') {
                return {noResultMessage: '施設は登録されていません。'}
            }
            return {noResultMessage: '検索結果に該当する情報がありませんでした。'}
        }

        return {
            totalCount: this.state.totalCount,
            rowsPerPage: this.state.rowsPerPage,
            page: this.state.page,
        }
    }

    renderFacilityTable(facilities: FacilityForAdmin[]): Nullable<ReactElement> {
        if (facilities.length === 0) {
            return null
        }
        const viewModeHeaders: SortableTableHeaderCell[] = [
            {id: 'facilityName', label: '施設名'},
            {id: 'categoryName', label: 'カテゴリ'},
            {id: 'sortOrder', label: '並び順', sortable: true, direction: this.getSortDirection('sortOrder')},
            {id: 'lotteryAvailability', label: '抽選可能'},
            {id: 'stayAvailability', label: '宿泊可能'},
            {id: 'releaseSetting', label: '非公開'},
            {
                id: 'updateDatetime',
                label: '更新日時',
                sortable: true,
                direction: this.getSortDirection('updateDatetime'),
            },
        ]

        const editHeaders: SortableTableHeaderCell[] = [
            ...viewModeHeaders,
            {id: 'action', label: '操作'},
        ]

        let headers: SortableTableHeaderCell[] = viewModeHeaders
        if (this.state.editMode) {
            headers = editHeaders
        }

        return (
            <SortablePaginationTable
                paginationProps={{
                    count: this.state.totalCount,
                    rowsPerPage: this.state.rowsPerPage,
                    page: this.state.page,
                    onPageChange: this.handleChangePage,
                    onRowsPerPageChange: this.handleChangeRowsPerPage,
                }}
            >
                <SortableTableHeader
                    noWrap
                    headers={headers}
                    onSortOrderChange={this.handleSortFacilities}
                />
                <SortablePaginationTableBody>
                    {this.state.facilities.map(facility => this.renderFacilityRecord(facility))}
                </SortablePaginationTableBody>
            </SortablePaginationTable>
        )
    }

    renderFacilityRecord(facility: FacilityForAdmin): ReactElement {
        const updatedDateTime: string = formatDateTimeForUpdateDate(facility.updateDatetime)
        let releaseStartDateTime: Nullable<string> = null
        let startDateTimeTooltip: Nullable<string> = null
        if (facility.releaseStartDateTime !== null) {
            releaseStartDateTime = this.formatForReleaseStartDateTime(facility.releaseStartDateTime)
            startDateTimeTooltip = '公開開始日時：' + this.formatForStartDateTimeTooltip(facility.releaseStartDateTime)
        }
        return (
            <TableRow key={facility.facilityId}>
                <TableCell>
                    {this.renderFacilityName(facility)}
                </TableCell>
                <TableCell>{facility.categoryName}</TableCell>
                <TableCell align={'right'}>{facility.sortOrder}</TableCell>
                <TableCell align={'center'}><CheckMark visible={facility.lotteryAvailability}/></TableCell>
                <TableCell align={'center'}><CheckMark visible={facility.stayAvailability}/></TableCell>
                <NoWrappedTableCell align={'center'}>
                    <Tooltip title={startDateTimeTooltip || ''} arrow placement={'right'}>
                        <div>
                            <div>
                                <CheckMark visible={!facility.releaseSetting}/>
                            </div>
                            <div>
                                {releaseStartDateTime}
                            </div>
                        </div>
                    </Tooltip>
                </NoWrappedTableCell>
                <NoWrappedTableCell align={'center'}>{updatedDateTime}</NoWrappedTableCell>
                {
                    this.state.editMode &&
                    <TableCell>
                        <AdminDefaultButton
                            size={'small'} onClick={this.handleDuplicateButtonClick.bind(this, facility)}>
                            複製
                        </AdminDefaultButton>
                    </TableCell>
                }
            </TableRow>
        )
    }

    formatForReleaseStartDateTime(stringWithoutTimeZone: string): string {
        const postDate = new Date(stringWithoutTimeZone)
        return moment(postDate.toISOString()).format('M/D H時')
    }

    formatForStartDateTimeTooltip(stringWithoutTimeZone: string): string {
        const postDate = new Date(stringWithoutTimeZone)
        return moment(postDate.toISOString()).format('YYYY/M/D H時')
    }

    renderFacilityName(facility: FacilityForAdmin): ReactNode {
        const facilityUrl: string = `/admin/condominiums/${this.props.condominiumCd}/facilities/${facility.facilityId}`
        const introductoryUrl: string = `/admin/condominiums/${this.props.condominiumCd}/facilities/${facility.facilityId}/introductory`

        if (this.state.facilityViewMode) {
            return <Link href={facilityUrl} underline="hover">{facility.facilityName}</Link>
        } else if (this.state.introductoryViewMode) {
            return <Link href={introductoryUrl} underline="hover">{facility.facilityName}</Link>
        } else {
            return <label>{facility.facilityName}</label>
        }
    }

    renderCategoriesSelect(): ReactNode {
        const categories = this.state.facilityCategories.map((category) => {
            return ({
                label: category.categoryName,
                value: `${category.categoryId}`,
            })
        })
        const categoryOptions = [{
            label: '全てのカテゴリ',
            value: 'all',
        }, ...categories]

        return (
            <PPSelect
                autoFocus
                eventEmitter={this.props.eventEmitter}
                data={categoryOptions}
                onChange={this.handleChangeCategorySelector}
                value={this.state.selectedCategory}
            />
        )
    }

    render(): ReactElement {
        return (
            <>
                <ListSearchFormArea
                    searchResult={this.searchResults()}
                    onEnterKey={this.handleCategorySelectedByEnterKey}
                >
                    <ConditionTable>
                        <ConditionTableBody>
                            <ConditionTableRow>
                                <ConditionCategoryCell>施設情報</ConditionCategoryCell>
                                <ConditionColumNameCell>カテゴリ</ConditionColumNameCell>
                                <ConditionInputCell>
                                    {this.renderCategoriesSelect()}
                                </ConditionInputCell>
                                <ConditionButtonCell>
                                    <AdminDefaultMediumButton onClick={this.handleSearchFacilities}>
                                        施設を検索する
                                    </AdminDefaultMediumButton>
                                </ConditionButtonCell>
                            </ConditionTableRow>
                        </ConditionTableBody>
                    </ConditionTable>
                </ListSearchFormArea>
                <SearchPageActionArea>
                    {
                        this.state.editMode &&
                        <AdminDefaultMediumButton onClick={this.handleNewFacilityButtonClick}>
                            新規
                        </AdminDefaultMediumButton>
                    }
                </SearchPageActionArea>
                <PageMainArea>
                    {this.renderFacilityTable(this.state.facilities)}
                </PageMainArea>
            </>
        )
    }
}

export default createAdminPage(AdminSearchFacilitiesPage)
